import React, { useContext, useEffect, useRef, useState } from 'react';
import { LangContext } from '../../contexts/langContext';
import { SocialContext } from '../../contexts/socialContext';
import { StateContext } from '../../contexts/stateContext';
import { visiblePentaPages } from '../Edu/PentaPages';

export const visiblePentaNarrativeIds = (pentaNarratives = {}, { version, memberCount = 2 } = {}) =>
  Object.keys(pentaNarratives)
    .filter((id) => {
      const narrative = pentaNarratives[id];
      if (!narrative || narrative.active === false) return false;
      if (narrative.beta && version !== 'β') return false;
      if (!narrative.user_choose) return false;
      if (memberCount > 2 && id === 'composite' && pentaNarratives.compare?.active !== false) {
        return false;
      }
      return true;
    })
    .sort((a, b) => (pentaNarratives[b].order || 0) - (pentaNarratives[a].order || 0));

export function PentaNarrativesList({
  variant = 'select',
  memberCount = 2,
}) {
  const context = { ...useContext(SocialContext), ...useContext(StateContext), ...useContext(LangContext) };
  const $pentaNarr = context.$pentaNarr;

  const options = visiblePentaNarrativeIds(context.pentaNarratives, {
    version: context.state.version,
    memberCount,
  });

  useEffect(() => {
    if (!options.length) return;
    if (!options.includes(context.pentaNarrativeCurrent)) {
      context.setPentaNarrativeCurrent(options[0]);
    }
  }, [options.join(','), context.pentaNarrativeCurrent]);

  if (options.length < 2) return null;

  const value = options.includes(context.pentaNarrativeCurrent)
    ? context.pentaNarrativeCurrent
    : options[0];

  if (variant === 'select') {
    return (
      <div className="x_guide_narratives">
        <select
          className="x_guide_narrative_select"
          value={value}
          onChange={(e) => context.setPentaNarrativeCurrent(e.target.value)}
          aria-label={$pentaNarr(value, 'trigger')}
        >
          {options.map((id) => (
            <option key={id} value={id}>
              {$pentaNarr(id, 'trigger')}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div className="x_edu_narrative_choose">
      {options.map((id) => (
        <div
          onClick={() => context.setPentaNarrativeCurrent(id)}
          key={id}
          className={context.pentaNarrativeCurrent === id ? 'x_edu_narrative1 active' : 'x_edu_narrative1'}
        >
          {$pentaNarr(id, 'trigger')}
        </div>
      ))}
      <div className="x_edu_narrative_choose_line" />
    </div>
  );
}

export function PentaGuide({
  narrative,
  memberCount = 2,
  scrollToSection,
}) {
  const context = { ...useContext(SocialContext), ...useContext(StateContext), ...useContext(LangContext) };
  const $ = context.$;

  const [activeLink, setActiveLink] = useState('');
  const [guideShown, setGuideShown] = useState(true);
  const guideRef = useRef(null);

  useEffect(() => {
    const probeY = () => window.innerHeight * 0.5;

    const updateActive = () => {
      const y = probeY();
      let bestId = '';
      let bestDist = Infinity;

      document.querySelectorAll('.x_edu_content0').forEach((el) => {
        if (!el.id) return;
        const rect = el.getBoundingClientRect();
        if (rect.height <= 0) return;
        const dist = y < rect.top ? rect.top - y : y > rect.bottom ? y - rect.bottom : 0;
        if (dist < bestDist) {
          bestDist = dist;
          bestId = el.id;
        }
      });

      if (bestId) setActiveLink(bestId);
    };

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        updateActive();
        ticking = false;
      });
    };

    updateActive();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [narrative]);

  const pages = visiblePentaPages(narrative?.pages, {
    memberCount,
    version: context.state.version,
    jyotishShown: context.state.jyotishShown,
  }).filter((one) => $('title', one) && !one.utility);

  return (
    <div className="x_guide00">
      <div className="x_guide0" ref={guideRef}>
        {!guideShown && (
          <div className="x_guide_trigger0" onClick={() => setGuideShown(true)}>
            <div className="x_guide_trigger">
              <div className="t_icon t_icon--20">
                <div>menu</div>
              </div>
            </div>
          </div>
        )}

        <div className={guideShown ? 'x_guide active' : 'x_guide'}>
          <PentaNarrativesList memberCount={memberCount} />

          <div className="x_theory_1 active">
            <div className="x_theo_ul">
              {pages.map((one) => {
                const oneId = one.id;
                return (
                  <div key={`penta_nav_${oneId}`}>
                    <div
                      className="x_theo_li active"
                      onClick={() => oneId ? scrollToSection(oneId) : void (0)}
                    >
                      {$('title', one)}
                      {activeLink === oneId && <div className="x_theo_active" />}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
